import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spin, Alert, Button, Table, Tag, message } from 'antd'
import { PrinterOutlined, FileTextOutlined, DollarOutlined, DownloadOutlined } from '@ant-design/icons'
import { formatPhoneNumber, formatPaymentTerms, jobPhotoReportCaption } from '@serviceflow/shared'
import { env } from '../config/environment'
import { getPublicInvoicePayContext } from '../utils/publicInvoicePayContext'
import { AffirmFinancingBlock } from '../components/financing/AffirmFinancingBlock'
import { LineItemNameWithThumbnail } from '../components/LineItemThumbnail'
import { amountDollarsToCents } from '@serviceflow/shared'

// Use centralized environment config - strips /api suffix for public routes
const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

interface PDFTemplateSettings {
  primaryColor: string
  fontFamily: 'default' | 'serif' | 'sans-serif' | 'modern'
  layoutStyle: 'classic' | 'modern' | 'minimal'
  logoPosition: 'left' | 'center' | 'right'
  showBusinessPhone: boolean
  showBusinessEmail: boolean
  showBusinessWebsite: boolean
  showCustomerPhone: boolean
  showServiceLocation: boolean
  showTermsSection: boolean
  showSignatureSection: boolean
  customFooterText: string
}

const defaultPDFSettings: PDFTemplateSettings = {
  primaryColor: '#1890ff',
  fontFamily: 'default',
  layoutStyle: 'classic',
  logoPosition: 'left',
  showBusinessPhone: true,
  showBusinessEmail: true,
  showBusinessWebsite: true,
  showCustomerPhone: true,
  showServiceLocation: true,
  showTermsSection: true,
  showSignatureSection: true,
  customFooterText: '',
}

const getFontFamily = (fontFamily: string) => {
  switch (fontFamily) {
    case 'serif':
      return 'Georgia, "Times New Roman", serif'
    case 'sans-serif':
      return 'Arial, Helvetica, sans-serif'
    case 'modern':
      return '"Inter", "Segoe UI", sans-serif'
    default:
      return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid':
      return 'success'
    case 'partial':
      return 'warning'
    case 'overdue':
      return 'error'
    case 'sent':
      return 'processing'
    default:
      return 'default'
  }
}

// Helper to parse address that might be JSON or string
const parseAddress = (address: any): { street?: string; city?: string; state?: string; zipCode?: string } | null => {
  if (!address) return null
  
  // If it's already an object
  if (typeof address === 'object' && address !== null) {
    return address
  }
  
  // If it's a string that looks like JSON
  if (typeof address === 'string') {
    // Check if it's JSON
    if (address.trim().startsWith('{')) {
      try {
        return JSON.parse(address)
      } catch {
        // Not valid JSON, return as street
        return { street: address }
      }
    }
    // Plain string address
    return { street: address }
  }
  
  return null
}

// Format business address from potentially JSON or string value
const formatBusinessAddress = (business: any): { street?: string; city?: string; state?: string; zipCode?: string } => {
  // First check if address is a JSON object
  const parsedAddress = parseAddress(business?.address)
  
  if (parsedAddress && (parsedAddress.street || parsedAddress.city)) {
    return parsedAddress
  }
  
  // Fallback to individual fields on business
  return {
    street: typeof business?.address === 'string' && !business.address.startsWith('{') ? business.address : undefined,
    city: business?.city,
    state: business?.state,
    zipCode: business?.zipCode
  }
}

export const PublicInvoicePdf: React.FC = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any>(null)
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      console.log('📄 PublicInvoicePdf: Starting fetch for token:', token)
      console.log('📄 API_URL:', API_URL)
      
      if (!token) {
        console.log('📄 No token provided')
        setError('No token provided')
        setLoading(false)
        return
      }

      const fetchUrl = `${API_URL}/api/public/pdf/${token}`
      console.log('📄 Fetching from:', fetchUrl)

      try {
        const response = await fetch(fetchUrl)
        console.log('📄 Response status:', response.status)
        
        const result = await response.json()
        console.log('📄 Response data:', result)

        if (!result.success) {
          console.log('📄 API returned error:', result.error)
          setError(result.error || 'Failed to load invoice')
          setLoading(false)
          return
        }

        if (result.type !== 'invoice') {
          console.log('📄 Invalid type:', result.type)
          setError('Invalid link type')
          setLoading(false)
          return
        }

        setData(result)
        setLoading(false)
      } catch (err) {
        console.error('📄 Error fetching public PDF:', err)
        setError('Failed to load invoice. Please try again.')
        setLoading(false)
      }
    }

    fetchData()
  }, [token])

  const pdfSettings = useMemo(() => {
    if (!data?.business?.settings) return defaultPDFSettings
    const settings = typeof data.business.settings === 'string' 
      ? JSON.parse(data.business.settings) 
      : data.business.settings
    return { ...defaultPDFSettings, ...settings?.pdfTemplateSettings }
  }, [data])

  const phoneFormat = useMemo(() => {
    if (!data?.business?.settings) return '(XXX) XXX-XXXX'
    const settings = typeof data.business.settings === 'string'
      ? JSON.parse(data.business.settings)
      : data.business.settings
    return settings?.phoneFormat || '(XXX) XXX-XXXX'
  }, [data])

  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return ''
    return formatPhoneNumber(phone, phoneFormat)
  }

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString()
  }

  const formatCurrency = (amount: string | number | undefined | null) => {
    if (amount === undefined || amount === null) return '$0.00'
    return `$${Number(amount).toFixed(2)}`
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPdf = async () => {
    if (!token) return
    setDownloadPdfLoading(true)
    try {
      const response = await fetch(`${API_URL}/api/public/pdf/${token}/pdf`)
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || 'Failed to download PDF')
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `invoice-${data?.invoice?.invoiceNumber ?? 'invoice'}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
      message.success('PDF downloaded')
    } catch (err: any) {
      message.error(err.message || 'Failed to download PDF')
    } finally {
      setDownloadPdfLoading(false)
    }
  }

  const handlePayNow = () => {
    if (!token) {
      message.error('Unable to process payment - missing token')
      return
    }
    // Navigate to selection page - user chooses card or ACH, then payment link is generated
    navigate(`/pay/select/${token}`)
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <Spin size="large" tip="Loading invoice..." />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5',
        padding: 24
      }}>
        <Alert 
          type="error" 
          message="Error" 
          description={error}
          showIcon
        />
      </div>
    )
  }

  const { invoice, customer, business, jobPhotos: jobPhotosRaw } = data
  const jobPhotos = Array.isArray(jobPhotosRaw) ? jobPhotosRaw : []

  const { totalAmount, paidAmount, balanceAmount, isDepositInvoice, payAmount, isPaid } =
    getPublicInvoicePayContext(invoice)

  const isSectionRow = (record: any) =>
    (record?.type || record?.itemType) === 'section'

  const itemColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => {
        if (isSectionRow(record)) {
          return <div style={{ fontWeight: 700 }}>{text}</div>
        }
        return (
        <LineItemNameWithThumbnail
          imageUrl={record.imageUrl}
          name={<div style={{ fontWeight: 500 }}>{text}</div>}
          description={
            record.description ? (
              <div style={{ color: '#666', fontSize: '12px' }}>{record.description}</div>
            ) : undefined
          }
        />
        )
      },
      onCell: (record: any) =>
        isSectionRow(record) ? { colSpan: 4, style: { background: '#f5f5f5' } } : {},
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
      render: (value: any, record: any) => (isSectionRow(record) ? null : value),
      onCell: (record: any) => (isSectionRow(record) ? { colSpan: 0 } : {}),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (value: number, record: any) => (isSectionRow(record) ? null : formatCurrency(value)),
      onCell: (record: any) => (isSectionRow(record) ? { colSpan: 0 } : {}),
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (_: any, record: any) => {
        if (isSectionRow(record)) return null
        const lineTotal = record.totalPrice || (record.quantity * record.unitPrice)
        return <strong>{formatCurrency(lineTotal)}</strong>
      },
      onCell: (record: any) => (isSectionRow(record) ? { colSpan: 0 } : {}),
    }
  ]

  return (
    <>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-container {
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }
          .invoice-document {
            box-shadow: none !important;
            border: none !important;
            padding: 20px !important;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @media screen {
          .print-container {
            background: #f5f5f5;
            min-height: 100vh;
            padding: 16px;
          }
        }
        @media screen and (max-width: 768px) {
          .print-container {
            padding: 8px;
          }
          .invoice-document {
            padding: 16px !important;
          }
          .pdf-header {
            flex-direction: column !important;
            text-align: center !important;
          }
          .pdf-header > div {
            order: unset !important;
            text-align: center !important;
            margin-bottom: 16px !important;
          }
          .totals-section {
            width: 100% !important;
          }
          .action-header {
            flex-direction: column !important;
            text-align: center !important;
          }
          .action-buttons {
            flex-direction: column !important;
            width: 100% !important;
          }
          .action-buttons button {
            width: 100% !important;
          }
          .pay-cta-section {
            padding: 16px !important;
          }
          .ant-table {
            font-size: 12px;
          }
          .ant-table-thead > tr > th,
          .ant-table-tbody > tr > td {
            padding: 8px 4px !important;
          }
        }
      `}</style>

      <div className="print-container">
        {/* Header with actions */}
        <div className="no-print" style={{ 
          maxWidth: 900, 
          margin: '0 auto 16px',
          padding: 16,
          background: 'white',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div 
            className="action-header"
            style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <FileTextOutlined style={{ fontSize: 24, color: pdfSettings.primaryColor }} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>Invoice #{invoice.invoiceNumber}</div>
                <Tag color={getStatusColor(invoice.status)}>{invoice.status?.toUpperCase()}</Tag>
              </div>
            </div>
            <div className="action-buttons" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Button 
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              >
                Print / Save PDF
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleDownloadPdf}
                loading={downloadPdfLoading}
              >
                Download PDF
              </Button>
              {!isPaid && (
                <Button 
                  type="primary" 
                  icon={<DollarOutlined />}
                  onClick={handlePayNow}
                  size="large"
                  style={{ 
                    backgroundColor: '#52c41a', 
                    borderColor: '#52c41a',
                    fontWeight: 600
                  }}
                >
                  {isDepositInvoice ? `Pay deposit — ${formatCurrency(payAmount)}` : `Pay Now - ${formatCurrency(balanceAmount)}`}
                </Button>
              )}
            </div>
          </div>

          {/* Payment status message */}
          {isPaid && (
            <Alert
              type="success"
              message="This invoice has been paid in full. Thank you!"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!isPaid && token && (
            <div style={{ marginTop: 16, maxWidth: 480 }}>
              <AffirmFinancingBlock
                amountCents={amountDollarsToCents(payAmount)}
                pdfToken={token}
                source="public_invoice_pdf"
                compact
              />
            </div>
          )}
          {invoice.status === 'partial' && !isDepositInvoice && (
            <Alert
              type="info"
              message={`Partial payment received. Remaining balance: ${formatCurrency(balanceAmount)}`}
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
          {isDepositInvoice && !isPaid && (
            <Alert
              type="info"
              message={
                <>
                  This invoice collects a <strong>deposit</strong> toward the project. You will pay{' '}
                  <strong>{formatCurrency(payAmount)}</strong> now (card only). The line items below reflect the full
                  project scope; your card will be charged the deposit amount, not the full balance shown in the totals.
                </>
              }
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>

        {/* Document */}
        <div 
          className="invoice-document"
          style={{
            maxWidth: 900,
            margin: '0 auto',
            background: 'white',
            padding: 40,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontFamily: getFontFamily(pdfSettings.fontFamily)
          }}
        >
          {/* Header */}
          {(() => {
            const businessAddr = formatBusinessAddress(business)
            return (
              <div 
                className="pdf-header"
                style={{ 
                  display: 'flex', 
                  justifyContent: pdfSettings.logoPosition === 'center' ? 'center' : 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 40,
                  flexDirection: pdfSettings.logoPosition === 'center' ? 'column' : 'row',
                  textAlign: pdfSettings.logoPosition === 'center' ? 'center' : 'left',
                  gap: 16,
                  flexWrap: 'wrap'
                }}>
                <div style={{ 
                  order: pdfSettings.logoPosition === 'right' ? 2 : 1,
                  marginBottom: pdfSettings.logoPosition === 'center' ? 20 : 0,
                  minWidth: 200
                }}>
                  {business?.logo ? (
                    <img 
                      src={business.logo} 
                      alt={business.name} 
                      style={{ maxHeight: 80, maxWidth: 200 }}
                    />
                  ) : (
                    <h1 style={{ 
                      margin: 0, 
                      fontSize: 24, 
                      color: pdfSettings.primaryColor 
                    }}>
                      {business?.name}
                    </h1>
                  )}
                  <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
                    {businessAddr.street && <div>{businessAddr.street}</div>}
                    {(businessAddr.city || businessAddr.state || businessAddr.zipCode) && (
                      <div>
                        {[businessAddr.city, businessAddr.state].filter(Boolean).join(', ')} {businessAddr.zipCode}
                      </div>
                    )}
                    {pdfSettings.showBusinessPhone && business?.phone && (
                      <div>{formatPhone(business.phone)}</div>
                    )}
                    {pdfSettings.showBusinessEmail && business?.email && (
                      <div>{business.email}</div>
                    )}
                  </div>
                </div>

                <div style={{ 
                  order: pdfSettings.logoPosition === 'right' ? 1 : 2,
                  textAlign: pdfSettings.logoPosition === 'center' ? 'center' : 'right',
                  minWidth: 200
                }}>
                  <h2 style={{ 
                    margin: 0, 
                    fontSize: 28, 
                    color: pdfSettings.primaryColor,
                    textTransform: 'uppercase',
                    letterSpacing: 2
                  }}>
                    Invoice
                  </h2>
                  <div style={{ marginTop: 8, fontSize: 14 }}>
                    <div><strong>Invoice #:</strong> {invoice.invoiceNumber}</div>
                    <div><strong>Issue Date:</strong> {formatDate(invoice.issuedAt)}</div>
                    <div><strong>Due Date:</strong> {formatDate(invoice.dueAt)}</div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Customer Info */}
          <div style={{ 
            marginBottom: 30,
            padding: 20,
            background: '#fafafa',
            borderRadius: 4
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: 14, 
              color: pdfSettings.primaryColor,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              Bill To
            </h3>
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 600 }}>
                {customer?.firstName} {customer?.lastName}
              </div>
              {(invoice.billingStreet || customer?.address) && (
                <div>{invoice.billingStreet || customer?.address}</div>
              )}
              {(invoice.billingCity || invoice.billingState || invoice.billingZipCode) && (
                <div>
                  {[invoice.billingCity, invoice.billingState].filter(Boolean).join(', ')} {invoice.billingZipCode}
                </div>
              )}
              {!(invoice.billingCity || invoice.billingState) && (customer?.city || customer?.state || customer?.zipCode) && (
                <div>
                  {[customer?.city, customer?.state].filter(Boolean).join(', ')} {customer?.zipCode}
                </div>
              )}
              {pdfSettings.showCustomerPhone && customer?.phone && (
                <div>{formatPhone(customer.phone)}</div>
              )}
              {customer?.email && <div>{customer.email}</div>}
            </div>
          </div>

          {/* Service Location */}
          {pdfSettings.showServiceLocation && (invoice.serviceStreet || invoice.serviceCity) && (
            <div style={{ marginBottom: 30 }}>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: 14, 
                color: pdfSettings.primaryColor,
                textTransform: 'uppercase',
                letterSpacing: 1
              }}>
                Service Location
              </h3>
              <div style={{ fontSize: 14, color: '#666' }}>
                {invoice.serviceStreet && <div>{invoice.serviceStreet}</div>}
                {(invoice.serviceCity || invoice.serviceState || invoice.serviceZipCode) && (
                  <div>
                    {[invoice.serviceCity, invoice.serviceState].filter(Boolean).join(', ')} {invoice.serviceZipCode}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Title & Description */}
          {(invoice.title || invoice.description) && (
            <div style={{ marginBottom: 30 }}>
              {invoice.title && (
                <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>{invoice.title}</h3>
              )}
              {invoice.description && (
                <p style={{ margin: 0, color: '#666' }}>{invoice.description}</p>
              )}
            </div>
          )}

          {/* Items Table */}
          {(() => {
            const businessData = business as any;
            const settings = businessData?.settings || {};
            const feePercent = settings.creditCardProcessingFeePercent;
            const hasFeeItem = invoice.items?.some((item: any) =>
              item.name?.toLowerCase().includes('credit card processing fee')
            );
            const amountForFee = isDepositInvoice
              ? payAmount
              : balanceAmount > 0
                ? balanceAmount
                : totalAmount;
            const feeAmount = feePercent && amountForFee > 0 ? (amountForFee * feePercent) / 100 : 0;

            if (feePercent && feePercent > 0) {
              return (
                <div style={{
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '15px',
                  fontSize: '13px',
                  color: '#0050b3'
                }}>
                  <strong>Credit card processing fee:</strong>{' '}
                  {hasFeeItem
                    ? `A ${feePercent}% fee has been added to this invoice as a line item.`
                    : isDepositInvoice
                      ? `Deposit payments are card-only and are subject to a ${feePercent}% processing fee${feeAmount > 0 ? ` (e.g. $${feeAmount.toFixed(2)} on the ${formatCurrency(payAmount)} deposit)` : ''}.`
                      : `Credit card payments are subject to a ${feePercent}% processing fee${feeAmount > 0 ? ` (e.g. $${feeAmount.toFixed(2)} on a balance of $${amountForFee.toFixed(2)})` : ''}. ACH/bank transfer payments do not include this fee.`}
                </div>
              );
            }
            return null;
          })()}
          <Table
            dataSource={invoice.items || []}
            columns={itemColumns}
            pagination={false}
            rowKey="id"
            size="small"
            style={{ marginBottom: 24 }}
          />

          {/* Totals */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            marginBottom: 30
          }}>
            <div className="totals-section" style={{ width: 300, maxWidth: '100%' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '8px 0',
                borderBottom: '1px solid #eee'
              }}>
                <span>Subtotal:</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              {Number(invoice.taxAmount) > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '8px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Tax:</span>
                  <span>{formatCurrency(invoice.taxAmount)}</span>
                </div>
              )}
              {Number(invoice.discountAmount) > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '8px 0',
                  borderBottom: '1px solid #eee',
                  color: '#52c41a'
                }}>
                  <span>Discount:</span>
                  <span>-{formatCurrency(invoice.discountAmount)}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '8px 0',
                borderBottom: '1px solid #eee',
                fontWeight: 600
              }}>
                <span>Total:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
              {paidAmount > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '8px 0',
                  borderBottom: '1px solid #eee',
                  color: '#52c41a'
                }}>
                  <span>Paid:</span>
                  <span>-{formatCurrency(paidAmount)}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '12px 0',
                fontWeight: 700,
                fontSize: 18,
                color: isPaid ? '#52c41a' : pdfSettings.primaryColor
              }}>
                <span>{isDepositInvoice ? 'Deposit due (pay now):' : 'Balance Due:'}</span>
                <span>{formatCurrency(isDepositInvoice ? payAmount : balanceAmount)}</span>
              </div>
              {isDepositInvoice && !isPaid && (
                <div style={{ fontSize: 12, color: '#666', textAlign: 'right', marginTop: -4 }}>
                  Project total on this invoice: {formatCurrency(totalAmount)}
                </div>
              )}
            </div>
          </div>

          {/* Payment Terms */}
          {invoice.paymentTerms && (
            <div style={{ marginBottom: 20 }}>
              <strong>Payment Terms:</strong> {formatPaymentTerms(invoice.paymentTerms)}
            </div>
          )}

          {jobPhotos.length > 0 && (
            <div className="job-photos-section" style={{ marginBottom: 30 }}>
              <h3
                style={{
                  margin: '0 0 12px 0',
                  fontSize: 14,
                  color: pdfSettings.primaryColor,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Attached photos
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 12,
                }}
              >
                {jobPhotos.map((photo: { id: string; url: string; description?: string | null; jobPhotoCategory?: string | null; category?: string | null; originalName?: string }) => {
                  const caption = jobPhotoReportCaption(photo)
                  return (
                    <div
                      key={photo.id}
                      className="job-photo-card"
                      style={{
                        border: '1px solid #e8e8e8',
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: '#fafafa',
                      }}
                    >
                      <a href={photo.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={photo.url}
                          alt={caption || 'Attached photo'}
                          style={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            display: 'block',
                            verticalAlign: 'top',
                          }}
                        />
                      </a>
                      {caption ? (
                        <div
                          style={{
                            padding: 8,
                            fontSize: 12,
                            color: '#555',
                            wordBreak: 'break-word',
                          }}
                        >
                          {caption}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          {pdfSettings.showTermsSection && invoice.terms && (
            <div style={{ 
              marginBottom: 30,
              padding: 16,
              background: '#fafafa',
              borderRadius: 4
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: pdfSettings.primaryColor }}>
                Terms & Conditions
              </h4>
              <div style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap' }}>
                {invoice.terms}
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div style={{ 
              marginBottom: 30,
              padding: 16,
              background: '#fffbe6',
              borderRadius: 4,
              border: '1px solid #ffe58f'
            }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Notes</h4>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {invoice.notes}
              </div>
            </div>
          )}

          {/* Footer */}
          {pdfSettings.customFooterText && (
            <div style={{ 
              marginTop: 40,
              paddingTop: 20,
              borderTop: '1px solid #eee',
              textAlign: 'center',
              fontSize: 12,
              color: '#888'
            }}>
              {pdfSettings.customFooterText}
            </div>
          )}

          {/* Pay Now CTA - visible in print too */}
          {!isPaid && (
            <div className="no-print pay-cta-section" style={{ 
              marginTop: 40,
              padding: 24,
              background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ color: 'white', fontSize: 18, marginBottom: 12 }}>
                {isDepositInvoice
                  ? `Pay your ${formatCurrency(payAmount)} deposit securely below.`
                  : 'Ready to pay? Click below to complete your payment securely.'}
              </div>
              <Button 
                type="primary" 
                size="large"
                icon={<DollarOutlined />}
                onClick={handlePayNow}
                style={{ 
                  backgroundColor: 'white', 
                  color: '#52c41a',
                  borderColor: 'white',
                  fontWeight: 600,
                  height: 48,
                  fontSize: 16
                }}
              >
                {isDepositInvoice ? `Pay ${formatCurrency(payAmount)} deposit` : `Pay ${formatCurrency(balanceAmount)} Now`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

